import { useEffect, useState } from "react";
import "./createuser.css";

function CreateUser({ apiBaseUrl = "https://agrisense.pro/smart-planting-backend" }) {
  const [form, setForm] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    role: "user",
  });
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [editingUserId, setEditingUserId] = useState(null);

  const normalizeUserData = (user) => ({
    id: user.id ?? user.user_id ?? user.userId ?? null,
    name: user.name ?? user.fullName ?? user.username ?? "Unnamed user",
    username: user.username ?? user.email ?? user.name ?? "unknown-user",
    role: user.role ?? "user",
  });

  const loadUsers = async () => {
    setLoadingUsers(true);
    setError("");

    try {
      const response = await fetch(`${apiBaseUrl}/get-users.php`, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || "Unable to load users.");
      }

      const resultUsers = Array.isArray(data.users)
        ? data.users
        : Array.isArray(data.data)
          ? data.data
          : [];

      setUsers(resultUsers.map((user) => normalizeUserData(user)));
    } catch (loadError) {
      setError(loadError.message || "Unable to load users right now.");
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [apiBaseUrl]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.username.trim() || !form.email.trim() || !form.password.trim()) {
      setError("Please fill in name, username, email, and password.");
      setMessage("");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError("Please enter a valid email address.");
      setMessage("");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch(`${apiBaseUrl}/create-user.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: form.name.trim(),
          username: form.username.trim(),
          email: form.email.trim(),
          password: form.password,
          role: form.role,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || "Unable to create user.");
      }

      setMessage(data.message || "User created successfully.");
      setForm({
        name: "",
        username: "",
        email: "",
        password: "",
        role: "user",
      });
      await loadUsers();
    } catch (createError) {
      setError(createError.message || "Unable to create user right now.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUserId(user.id ?? null);
    setForm({
      name: user.name || "",
      username: user.username || "",
      email: user.email || "",
      password: "",
      role: user.role || "user",
    });
    setError("");
    setMessage("");
  };

  const handleUpdateUser = async () => {
    if (!editingUserId) {
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch(`${apiBaseUrl}/update-user.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: editingUserId,
          name: form.name.trim(),
          username: form.username.trim(),
          email: form.email.trim(),
          password: form.password || undefined,
          role: form.role,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || "Unable to update user.");
      }

      setMessage(data.message || "User updated successfully.");
      setEditingUserId(null);
      setForm({ name: "", username: "", email: "", password: "", role: "user" });
      await loadUsers();
    } catch (updateError) {
      setError(updateError.message || "Unable to update user right now.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!user || (!user.id && !user.username)) {
      return;
    }

    const isConfirmed = window.confirm(`Delete ${user.name || user.username}?`);
    if (!isConfirmed) {
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch(`${apiBaseUrl}/delete-user.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: user.id,
          username: user.username,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || "Unable to delete user.");
      }

      setMessage(data.message || "User deleted successfully.");
      if (editingUserId === user.id) {
        setEditingUserId(null);
        setForm({ name: "", username: "", email: "", password: "", role: "user" });
      }
      await loadUsers();
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete user right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-management-panel">
      <div className="create-user-panel">
        <div className="panel-header-row">
          <div>
            <p className="eyebrow">Administrator</p>
            <h3>{editingUserId ? "Edit user" : "Create new user"}</h3>
          </div>
        </div>

        <form className="create-user-form" onSubmit={editingUserId ? (event) => { event.preventDefault(); handleUpdateUser(); } : handleSubmit}>
          <div className="field-row">
            <div className="field-group">
              <label htmlFor="user-name">Full name</label>
              <input
                id="user-name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter full name"
              />
            </div>

            <div className="field-group">
              <label htmlFor="user-role">Role</label>
              <select id="user-role" name="role" value={form.role} onChange={handleChange}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>

          <div className="field-row">
            <div className="field-group">
              <label htmlFor="user-username">Username</label>
              <input
                id="user-username"
                name="username"
                type="text"
                value={form.username}
                onChange={handleChange}
                placeholder="Enter username"
              />
            </div>

            <div className="field-group">
              <label htmlFor="user-email">Email address</label>
              <input
                id="user-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter email address"
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field-group">
              <label htmlFor="user-password">Password</label>
              <input
                id="user-password"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                placeholder={editingUserId ? "Leave empty to keep current password" : "Enter password"}
              />
            </div>
          </div>

          {error ? <div className="alert-box">{error}</div> : null}
          {message ? <div className="success-box">{message}</div> : null}

          <div className="form-actions">
            <button className="primary-button" type="submit" disabled={loading}>
              {loading ? (editingUserId ? "Updating user..." : "Creating user...") : (editingUserId ? "Update user" : "Create user")}
            </button>
            {editingUserId ? (
              <button
                className="secondary-button"
                type="button"
                onClick={() => {
                  setEditingUserId(null);
                  setForm({ name: "", username: "", email: "", password: "", role: "user" });
                  setError("");
                  setMessage("");
                }}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      </div>

      <div className="users-panel">
        <div className="panel-header-row">
          <div>
            <p className="eyebrow">System</p>
            <h3>Account directory</h3>
          </div>
        </div>

        {loadingUsers ? (
          <p className="muted-text">Loading users...</p>
        ) : users.length === 0 ? (
          <p className="muted-text">No users found.</p>
        ) : (
          <div className="user-list" role="list">
            {users.map((user) => (
              <div className="user-card" key={user.id ?? `${user.username}-${user.name}`} role="listitem">
                <div className="user-info">
                  <strong>{user.name || user.username || "Unnamed user"}</strong>
                  <p>@{user.username || "unknown-user"}</p>
                </div>

                <div className="user-actions">
                  <span className={`role-badge ${user.role === "admin" ? "admin" : "user"}`}>
                    {user.role || "user"}
                  </span>
                  <button type="button" className="mini-button edit" onClick={() => handleEditClick(user)}>
                    Edit
                  </button>
                  <button type="button" className="mini-button delete" onClick={() => handleDeleteUser(user)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateUser;
